/**
 * work-permit.ts
 * locx-permisos-de-trabajo PR1 (A1) — Zod contracts for servicios.ptw_type,
 * the LOCX Permisos de Trabajo (PTW) config anchor.
 *
 * Closed vocabularies enforced here mirror the DB CHECK constraints in
 * migration 0166 (defense-in-depth backstop — servicios/CLAUDE.md red line #1):
 *   maxValidityUnit:      'hours' | 'days'
 *   revalidationTrigger:  'turno_fijo' | 'cambio_de_actor'
 *
 * Only ptw_type ships in this PR — work_permit and the rest of the PTW family
 * land in later PRs of the same change (tasks.md PR2 onward). This file will
 * grow with them; the name is `work-permit.ts` (not `ptw-type.ts`) because
 * that is the eventual home for the whole PTW contract surface, per
 * design.md's file-changes table.
 *
 * Spec: openspec/changes/locx-permisos-de-trabajo/design.md §Schema Drizzle Slice A
 * Tasks: openspec/changes/locx-permisos-de-trabajo/tasks.md PR1 (A1), task 1.8
 */
import { z } from 'zod';

import { DataTypeSchema } from './configurable-engine.js';

// ---------------------------------------------------------------------------
// Closed vocabularies (design.md §Schema Drizzle Slice A)
// ---------------------------------------------------------------------------

export const PTW_MAX_VALIDITY_UNITS = ['hours', 'days'] as const;
export const PtwMaxValidityUnitSchema = z.enum(PTW_MAX_VALIDITY_UNITS, {
  errorMap: () => ({ message: 'maxValidityUnit must be one of: hours, days' }),
});
export type PtwMaxValidityUnit = z.infer<typeof PtwMaxValidityUnitSchema>;

export const PTW_REVALIDATION_TRIGGERS = ['turno_fijo', 'cambio_de_actor'] as const;
export const PtwRevalidationTriggerSchema = z.enum(PTW_REVALIDATION_TRIGGERS, {
  errorMap: () => ({ message: 'revalidationTrigger must be one of: turno_fijo, cambio_de_actor' }),
});
export type PtwRevalidationTrigger = z.infer<typeof PtwRevalidationTriggerSchema>;

// ---------------------------------------------------------------------------
// ptw_type — admin create/response contracts
// ---------------------------------------------------------------------------
// companyId/active/createdAt/workflow are never in request bodies — companyId
// comes from TenantContext (KC-3.5), active/createdAt/workflow are DB-defaulted.
// code is immutable after creation (same posture as ServiceType/FailureType).
// ---------------------------------------------------------------------------

export const PtwTypeCreateSchema = z
  .object({
    code: z.string().min(1, 'code is required').max(128, 'code must be at most 128 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    maxValidityValue: z.coerce.number().int().positive('maxValidityValue must be a positive integer'),
    maxValidityUnit: PtwMaxValidityUnitSchema,
    shiftsPerDay: z.coerce.number().int().positive('shiftsPerDay must be a positive integer').optional(),
    revalidationTrigger: PtwRevalidationTriggerSchema,
  })
  .strict();
export type PtwTypeCreate = z.infer<typeof PtwTypeCreateSchema>;

export const PtwTypeResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  code: z.string(),
  label: z.string(),
  maxValidityValue: z.number().int().positive(),
  maxValidityUnit: PtwMaxValidityUnitSchema,
  shiftsPerDay: z.number().int().positive(),
  revalidationTrigger: PtwRevalidationTriggerSchema,
  workflow: z.string(),
  active: z.boolean(),
  createdAt: z.string().datetime().or(z.date()),
});
export type PtwTypeResponse = z.infer<typeof PtwTypeResponseSchema>;

export const PtwTypeListResponseSchema = z.object({
  items: z.array(PtwTypeResponseSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type PtwTypeListResponse = z.infer<typeof PtwTypeListResponseSchema>;

// ---------------------------------------------------------------------------
// work_permit — root aggregate create/response contracts (PR2 / A2)
// ---------------------------------------------------------------------------
// companyId/currentStateId/permitNumber/createdAt/createdBy are never in
// request bodies: permitNumber is server-generated (CreateWorkPermitUseCase,
// PDT-<CODE>-NNN), companyId/createdBy come from TenantContext (KC-3.5),
// currentStateId starts at EN_ELABORACION (KI-7). equipmentSummary/Detail
// maxLength mirror the DB CHECK constraints in migration 0167 (defense-in-
// depth backstop, servicios/CLAUDE.md red line #1) — design.md's explicit
// values (200/4000), NOT the 38/80 figures from the launch prompt, which do
// not appear anywhere in proposal.md (verified — see migration 0167 header).
// serviceOrderId/assetId/clientId are all optional (proposal.md decision 1).
// ---------------------------------------------------------------------------

/**
 * huecos-de-lectura-frontend (Slice H / PR10), gap 1 — one dynamic EAV field
 * value, mirrors `PtwFieldValueWriteInput` in
 * `application/CreateWorkPermitUseCase.ts` EXACTLY (verified against that
 * interface's real signature before choosing this shape — it is an ARRAY
 * keyed by `ptwFieldDefinitionId`, unlike ServiceOrderCreateSchema's `fields`
 * which is a `Record<key, DynamicFieldValue>` resolved key→id by the route).
 * `value` excludes `Date` (never appears over the wire — JSON has no Date
 * type); `PtwFieldValueRepository.buildEavColumns` already coerces a
 * date/datetime string via `new Date(String(value))`.
 */
export const PtwFieldValueWriteInputSchema = z
  .object({
    ptwFieldDefinitionId: z.string().uuid('ptwFieldDefinitionId must be a valid UUID'),
    dataType: DataTypeSchema,
    value: z.union([z.string(), z.number(), z.boolean()]),
  })
  .strict();
export type PtwFieldValueWriteInput = z.infer<typeof PtwFieldValueWriteInputSchema>;

export const WorkPermitCreateSchema = z
  .object({
    ptwTypeId: z.string().uuid('ptwTypeId must be a valid UUID'),
    workDescription: z.string().min(1, 'workDescription is required'),
    validFrom: z.string().datetime({ message: 'validFrom must be an ISO datetime string' }),
    validTo: z.string().datetime({ message: 'validTo must be an ISO datetime string' }).optional(),
    serviceOrderId: z.string().uuid('serviceOrderId must be a valid UUID').optional(),
    assetId: z.string().uuid('assetId must be a valid UUID').optional(),
    clientId: z.string().uuid('clientId must be a valid UUID').optional(),
    equipmentSummary: z.string().max(200, 'equipmentSummary must be at most 200 characters').optional(),
    equipmentDetail: z.string().max(4000, 'equipmentDetail must be at most 4000 characters').optional(),
    legalEntityId: z.string().uuid('legalEntityId must be a valid UUID').optional(),
    /**
     * huecos-de-lectura-frontend (Slice H / PR10), gap 1 — atomic
     * write-with-parent of the `fields` EAV map. `CreateWorkPermitUseCase`
     * has supported this since PR4 (B2, task 4.4); it was simply never
     * exposed on the HTTP contract. Optional — omitting it is
     * backward-compatible with every existing caller.
     */
    fields: z.array(PtwFieldValueWriteInputSchema).optional(),
    /**
     * cross-tenant-kernel-mechanism PR8 — the OPERATOR tenant this permit is
     * "for", if any. Optional: a permit with no counterparty is the existing
     * normal case (single-tenant, no cross-company approval needed) and stays
     * byte-identical. When provided, `CreateWorkPermitUseCase` validates it
     * against an ACTIVE `kernel-tenant-partnership` where the CALLING tenant
     * is the owner side and this id is the counterparty — rejecting an
     * arbitrary company id here would let any tenant expose its own permits
     * to a company it never partnered with (PTW_COUNTERPARTY_NOT_PARTNERED).
     */
    counterpartyCompanyId: z.string().uuid('counterpartyCompanyId must be a valid UUID').optional(),
  })
  .strict();
export type WorkPermitCreate = z.infer<typeof WorkPermitCreateSchema>;

// riskLevel (design.md §D5) — closed vocabulary, NEVER accepted in
// WorkPermitCreateSchema/WorkPermitDocumentCreateSchema (both `.strict()`):
// it is written EXCLUSIVELY by domain/work-permit-risk.ts's computeRiskLevel(),
// inside AttachWorkPermitDocumentUseCase's transaction (PR5, task 5.6/5.8).
export const PTW_RISK_LEVELS = ['bajo', 'medio', 'alto'] as const;
export const PtwRiskLevelSchema = z.enum(PTW_RISK_LEVELS, {
  errorMap: () => ({ message: `riskLevel must be one of: ${PTW_RISK_LEVELS.join(', ')}` }),
});
export type PtwRiskLevel = z.infer<typeof PtwRiskLevelSchema>;

export const WorkPermitResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  ptwTypeId: z.string().uuid(),
  permitNumber: z.string(),
  currentStateId: z.string().uuid(),
  currentState: z.string(),
  serviceOrderId: z.string().uuid().nullable(),
  assetId: z.string().uuid().nullable(),
  clientId: z.string().uuid().nullable(),
  /**
   * cross-tenant-kernel-mechanism PR3a (migration 0181) — the OPERATOR
   * tenant authorized to read/approve this permit under an active
   * kernel-tenant-partnership. `null` = no cross-tenant path, behavior is
   * byte-identical to before this column existed (spec
   * locx-work-permit-domain, "permit without a counterparty behaves
   * unchanged"). Optional (not just nullable) — same backward-compatible
   * posture as `WorkPermitCreateSchema.fields` above: omitting it does not
   * break any existing caller/fixture that predates this field.
   */
  counterpartyCompanyId: z.string().uuid().nullable().optional(),
  workDescription: z.string(),
  equipmentSummary: z.string().nullable(),
  equipmentDetail: z.string().nullable(),
  validFrom: z.string().datetime().or(z.date()),
  validTo: z.string().datetime().or(z.date()).nullable(),
  riskLevel: PtwRiskLevelSchema.nullable(),
  legalEntityId: z.string().uuid().nullable(),
  createdBy: z.string().uuid(),
  /**
   * Resolved display name for `createdBy` ("Solicitante" — who requested this
   * permit), same server-side resolve-to-human-name posture as
   * ServiceOrderDetailResponse's `createdByName` (apps/frontend/CLAUDE.md:
   * never render a raw uuid). `null` when the user id can't be resolved
   * (cross-tenant row whose owner isn't an active partner, or an orphaned
   * id) — degrades gracefully, never a hard error on a read.
   */
  createdByName: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  updatedAt: z.string().datetime().or(z.date()),
  /**
   * READ-DERIVED ONLY — never persisted, never settable by any request body
   * (this schema is response-only). Computed fresh on every read from
   * `ptw_type.maxValidityValue`/`maxValidityUnit` vs. the most recent
   * transition into EN_EJECUCION (spec locx-work-permit-domain, "Vigencia
   * Máxima Is a Read-Derived State, Never a Persisted Transition" — post-
   * sdd-verify fix, see tasks.md "## Fixes post sdd-verify"). `false` for any
   * permit not currently EN_EJECUCION, regardless of elapsed time.
   */
  vencido: z.boolean(),
});
export type WorkPermitResponse = z.infer<typeof WorkPermitResponseSchema>;

/**
 * huecos-de-lectura-frontend (Slice H / PR10), gap 3 — GET /locx/work-permits
 * paginated listing. Same envelope shape as PtwTypeListResponseSchema/
 * AtsListResponseSchema (page/pageSize/totalCount/totalPages) —
 * @suite/contracts-kernel's `buildPaginatedResponse` precedent.
 */
export const WorkPermitListResponseSchema = z.object({
  items: z.array(WorkPermitResponseSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type WorkPermitListResponse = z.infer<typeof WorkPermitListResponseSchema>;

/**
 * cross-tenant-kernel-mechanism PR3a — query contract for the "operator
 * filters by contractor" case of `GET /locx/work-permits` (spec
 * kernel-tenant-partnership / "Partnership-Aware Listado for the
 * Counterparty"). Contract only — the route itself is wired in PR6, same
 * posture as the ptw-listado-filter-vocabulary `area`/`gerencia`/`disciplina`
 * query params. `contractorCompanyId` is validated as a UUID here because,
 * unlike those closed-vocabulary filters, this one selects across a tenant
 * boundary and is worth a strict shape check before it ever reaches
 * `WorkPermitRepository.listAsCounterparty`.
 */
export const WorkPermitContractorFilterQuerySchema = z
  .object({
    contractorCompanyId: z.string().uuid('contractorCompanyId must be a valid UUID').optional(),
  })
  .strict();
export type WorkPermitContractorFilterQuery = z.infer<typeof WorkPermitContractorFilterQuerySchema>;

/**
 * cross-tenant-kernel-mechanism PR6 — `GET /locx/work-permits/contractors`.
 * Every contractor tenant that has granted the CALLING (operator) tenant an
 * active `kernel-tenant-partnership` for `locx.work_permit`, with a resolved
 * human NAME (never a raw `companyId` — apps/frontend/CLAUDE.md, "show human
 * labels, never raw keys"). Backs the listado's contractor column/`Combobox`
 * filter AND the "does this tenant have at least one active partnership as
 * counterparty at all" gate — an empty `items[]` means no, hide both.
 */
export const WorkPermitContractorOptionSchema = z.object({
  companyId: z.string().uuid(),
  name: z.string(),
});
export type WorkPermitContractorOption = z.infer<typeof WorkPermitContractorOptionSchema>;

export const WorkPermitContractorListResponseSchema = z.object({
  items: z.array(WorkPermitContractorOptionSchema),
});
export type WorkPermitContractorListResponse = z.infer<typeof WorkPermitContractorListResponseSchema>;

/**
 * cross-tenant-kernel-mechanism PR8 — `GET /locx/work-permits/counterparties`.
 * Mirror of `WorkPermitContractorOptionSchema`/`WorkPermitContractorListResponseSchema`
 * above, but the OPPOSITE direction: every OPERATOR tenant the CALLING
 * (contractor) tenant has an ACTIVE `kernel-tenant-partnership` with, as the
 * OWNER side (`listMyPartnerships` filtered to `ownerCompanyId = self`,
 * `status = 'active'`), resolved to a human NAME (never a raw `companyId` —
 * apps/frontend/CLAUDE.md). Backs `PermisoCreateView.vue`'s optional "¿Para
 * qué empresa es este permiso?" Combobox AND its own visibility gate — an
 * empty `items[]` means "no active partnership as owner", hide the field.
 */
export const WorkPermitCounterpartyOptionSchema = z.object({
  companyId: z.string().uuid(),
  name: z.string(),
});
export type WorkPermitCounterpartyOption = z.infer<typeof WorkPermitCounterpartyOptionSchema>;

export const WorkPermitCounterpartyListResponseSchema = z.object({
  items: z.array(WorkPermitCounterpartyOptionSchema),
});
export type WorkPermitCounterpartyListResponse = z.infer<typeof WorkPermitCounterpartyListResponseSchema>;

/**
 * huecos-de-lectura-frontend (Slice H / PR10), gap 4 — GET
 * /locx/work-permits/counts-by-state dashboard tiles. `{ [stateName]: count }`
 * for the current tenant — mirrors GetFleetFailureDashboardUseCase's
 * `countsByState` shape (FleetFailureDashboardResponse), but as a plain
 * record (not a closed-key object) because WORK_PERMIT_TRANSITION_TARGET_STATES
 * plus the initial EN_ELABORACION state make 9 possible buckets and this
 * endpoint's contract, unlike the fleet-failure one, is not zero-filling every
 * bucket — only states with at least one permit ever appear.
 */
export const WorkPermitCountsByStateResponseSchema = z.record(z.string(), z.number().int().nonnegative());
export type WorkPermitCountsByStateResponse = z.infer<typeof WorkPermitCountsByStateResponseSchema>;

// ---------------------------------------------------------------------------
// ptw_field_definition / ptw_field_option — THIRD EAV family (PR3 / B1)
// ---------------------------------------------------------------------------
// Mirrors FieldDefinitionCreateSchema/FieldDefinitionAdminResponseSchema
// (configurable-engine.ts) closely — see design.md §D1 for why this is a NEW,
// independent pair rather than a generalisation of field_definition.
//
// PTW_HAZARD_KEYS is duplicated (not imported) from
// packages/modules/servicios/src/domain/ptw-hazard-vocabulary.ts — contracts-*
// packages never depend on packages/modules/* (module boundary contract runs
// the other way). Same duplication posture already used for
// PTW_MAX_VALIDITY_UNITS/PTW_REVALIDATION_TRIGGERS above (DB CHECK + Zod both
// hand-kept in sync — design.md §D4 / servicios/CLAUDE.md red line #1).
// ---------------------------------------------------------------------------

export const PTW_HAZARD_KEYS = [
  'gases',
  'izaje',
  'altura',
  'espacio_confinado',
  'caliente',
  'electrico',
  'excavacion',
] as const;
export const PtwHazardKeySchema = z.enum(PTW_HAZARD_KEYS, {
  errorMap: () => ({ message: `hazardKey must be one of: ${PTW_HAZARD_KEYS.join(', ')}` }),
});
export type PtwHazardKey = z.infer<typeof PtwHazardKeySchema>;

// PTW_LISTADO_FILTER_KEYS is duplicated (not imported) from
// packages/modules/servicios/src/domain/ptw-listado-filter-vocabulary.ts —
// same cross-package duplication posture as PTW_HAZARD_KEYS above (DB CHECK
// + Zod both hand-kept in sync — migration 0176 / locx-permisos-de-trabajo-frontend
// F2b.2 gap closure).
export const PTW_LISTADO_FILTER_KEYS = ['area', 'gerencia', 'disciplina'] as const;
export const PtwListadoFilterKeySchema = z.enum(PTW_LISTADO_FILTER_KEYS, {
  errorMap: () => ({ message: `listFilterKey must be one of: ${PTW_LISTADO_FILTER_KEYS.join(', ')}` }),
});
export type PtwListadoFilterKey = z.infer<typeof PtwListadoFilterKeySchema>;

export const PTW_FIELD_OWNER_KINDS = ['work_permit', 'ats'] as const;
export const PtwFieldOwnerKindSchema = z.enum(PTW_FIELD_OWNER_KINDS, {
  errorMap: () => ({ message: 'ownerKind must be one of: work_permit, ats' }),
});
export type PtwFieldOwnerKind = z.infer<typeof PtwFieldOwnerKindSchema>;

/**
 * Well-known `groupKey` for gas `ptw_field_definition` rows (design.md D2 open
 * question, resolved by task 3b-i.3 of locx-permisos-de-trabajo-frontend).
 * These rows configure the gases read during an atmosphere reading (min/max
 * bounds per gas) — they are metadata for `AtmosphereReadingPanel`, never a
 * create-form input, even though their `ownerKind` is `'work_permit'` like
 * every other work-permit field.
 *
 * Confirmed NOT discriminated server-side (verified against the real routes
 * on this branch, `packages/modules/servicios/src/http/ptw-field-definition.
 * routes.ts`): `GET /locx/ptw-types/:id/field-definitions` has no exclusion
 * query param, and `ownerKind` alone cannot tell a gas field apart from an
 * ordinary work-permit field. This constant is the fallback design.md names
 * explicitly ("se exporta la constante desde contracts-servicios y se filtra
 * por ella") — the ONE named place client code compares against, instead of
 * a raw `'gas'` string literal scattered at call sites.
 */
export const PTW_GAS_GROUP_KEY = 'gas';

/**
 * Request body for creating a new ptw_field_definition (admin only,
 * `locx.permiso.administrar`). companyId is NOT here — TenantContext (KC-3.5).
 * ownerKind defaults to 'work_permit' (the only value this PR writes; 'ats'
 * lands in PR9).
 */
export const PtwFieldDefinitionCreateSchema = z
  .object({
    ptwTypeId: z.string().uuid('ptwTypeId must be a valid UUID'),
    ownerKind: PtwFieldOwnerKindSchema.optional().default('work_permit'),
    key: z.string().min(1, 'key is required').max(128, 'key must be at most 128 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    /** Closed vocabulary — red line #1. Same 8-value enum as field_definition/asset_attribute_def. */
    dataType: DataTypeSchema,
    /** Closed vocabulary (design.md §D4). Omit/undefined = informative field, no approval guard. */
    hazardKey: PtwHazardKeySchema.optional(),
    /** Closed vocabulary (migration 0176). Omit/undefined = not a listado filter field. */
    listFilterKey: PtwListadoFilterKeySchema.optional(),
    groupKey: z.string().max(64).optional(),
    unit: z.string().max(32).optional(),
    required: z.boolean().optional().default(false),
    defaultValue: z.string().optional(),
    position: z.number().int().optional().default(0),
    minValue: z.string().optional(),
    maxValue: z.string().optional(),
    maxLength: z.number().int().optional(),
    pattern: z.string().optional(),
    /**
     * Explicit layout position (migration 0175) — ALL OPTIONAL/NULLABLE. If a
     * tenant does not configure these, rendering falls back to the CURRENT
     * behavior (ordered by `position`, no fixed row/column). No range
     * validation here: a grid's valid column count is a rendering/UI concern,
     * not a contract-level invariant.
     */
    layoutRow: z.number().int().optional().nullable(),
    layoutColumn: z.number().int().optional().nullable(),
    layoutColSpan: z.number().int().optional().nullable(),
  })
  .strict();
export type PtwFieldDefinitionCreate = z.infer<typeof PtwFieldDefinitionCreateSchema>;

/**
 * One ptw_field_option row as returned in admin responses.
 */
export const PtwFieldOptionAdminSchema = z.object({
  id: z.string().uuid(),
  ptwFieldDefinitionId: z.string().uuid(),
  value: z.string(),
  label: z.string(),
  position: z.number().int(),
});
export type PtwFieldOptionAdmin = z.infer<typeof PtwFieldOptionAdminSchema>;

/** Request body for adding an option to a ptw_field_definition (dataType must be 'select'). */
export const PtwFieldOptionCreateSchema = z
  .object({
    value: z.string().min(1, 'value is required').max(128),
    label: z.string().min(1, 'label is required').max(256),
    position: z.number().int().optional().default(0),
  })
  .strict();
export type PtwFieldOptionCreate = z.infer<typeof PtwFieldOptionCreateSchema>;

/**
 * Admin response for a single ptw_field_definition — raw row (never calls
 * PtwFieldResolver — includes inactive rows, same posture as
 * FieldDefinitionAdminResponseSchema).
 */
export const PtwFieldDefinitionAdminResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  ptwTypeId: z.string().uuid(),
  ownerKind: PtwFieldOwnerKindSchema,
  key: z.string(),
  label: z.string(),
  dataType: DataTypeSchema,
  hazardKey: PtwHazardKeySchema.nullable(),
  listFilterKey: PtwListadoFilterKeySchema.nullable(),
  groupKey: z.string().nullable(),
  unit: z.string().nullable(),
  required: z.boolean(),
  defaultValue: z.string().nullable(),
  position: z.number().int(),
  minValue: z.string().nullable(),
  maxValue: z.string().nullable(),
  maxLength: z.number().int().nullable(),
  pattern: z.string().nullable(),
  active: z.boolean(),
  layoutRow: z.number().int().nullable(),
  layoutColumn: z.number().int().nullable(),
  layoutColSpan: z.number().int().nullable(),
  options: z.array(PtwFieldOptionAdminSchema),
});
export type PtwFieldDefinitionAdminResponse = z.infer<typeof PtwFieldDefinitionAdminResponseSchema>;

export const PtwFieldDefinitionAdminListResponseSchema = z.object({
  items: z.array(PtwFieldDefinitionAdminResponseSchema),
});
export type PtwFieldDefinitionAdminListResponse = z.infer<typeof PtwFieldDefinitionAdminListResponseSchema>;

/**
 * huecos-de-lectura-frontend (Slice H / PR10), gap 5 — one assembled typed EAV
 * value for GET /locx/work-permits/:id/field-values. Mirrors
 * `PtwFieldValueRecord` in `infrastructure/repositories/PtwFieldValueRepository.ts`
 * (the pivot-to-DTO output shape) — the EAV pivot stays exclusively in
 * infrastructure (LOCX CLAUDE.md); this is the assembled, response-only DTO,
 * never a raw `value_*` column. `value` excludes `Date` because it is
 * serialized as an ISO string in the response (same posture as every other
 * `.datetime().or(z.date())` — this one is response-only so it is always
 * ALREADY a string/number/boolean by the time it reaches this schema).
 */
export const PtwFieldValueResponseSchema = z.object({
  ptwFieldDefinitionId: z.string().uuid(),
  dataType: DataTypeSchema,
  value: z.union([z.string(), z.number(), z.boolean()]),
});
export type PtwFieldValueResponse = z.infer<typeof PtwFieldValueResponseSchema>;

export const PtwFieldValueListResponseSchema = z.object({
  items: z.array(PtwFieldValueResponseSchema),
});
export type PtwFieldValueListResponse = z.infer<typeof PtwFieldValueListResponseSchema>;

// ---------------------------------------------------------------------------
// ptw_catalog_entry — ONE catalog for THREE identical shapes (design.md §D3,
// PR5 / Slice C). `kind` is a CLOSED, code-owned vocabulary (CHECK, migration
// 0170) — document_type (this PR), closure_kind (PR6), authority_role (PR7).
// ---------------------------------------------------------------------------

export const PTW_CATALOG_ENTRY_KINDS = ['document_type', 'closure_kind', 'authority_role'] as const;
export const PtwCatalogEntryKindSchema = z.enum(PTW_CATALOG_ENTRY_KINDS, {
  errorMap: () => ({ message: `kind must be one of: ${PTW_CATALOG_ENTRY_KINDS.join(', ')}` }),
});
export type PtwCatalogEntryKind = z.infer<typeof PtwCatalogEntryKindSchema>;

/** Request body for creating a ptw_catalog_entry (admin only, `locx.permiso.administrar`). */
export const PtwCatalogEntryCreateSchema = z
  .object({
    kind: PtwCatalogEntryKindSchema,
    code: z.string().min(1, 'code is required').max(64, 'code must be at most 64 characters'),
    label: z.string().min(1, 'label is required').max(256, 'label must be at most 256 characters'),
    position: z.number().int().optional().default(0),
  })
  .strict();
export type PtwCatalogEntryCreate = z.infer<typeof PtwCatalogEntryCreateSchema>;

export const PtwCatalogEntryResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  kind: PtwCatalogEntryKindSchema,
  code: z.string(),
  label: z.string(),
  position: z.number().int(),
  active: z.boolean(),
});
export type PtwCatalogEntryResponse = z.infer<typeof PtwCatalogEntryResponseSchema>;

export const PtwCatalogEntryListResponseSchema = z.object({
  items: z.array(PtwCatalogEntryResponseSchema),
});
export type PtwCatalogEntryListResponse = z.infer<typeof PtwCatalogEntryListResponseSchema>;

// ---------------------------------------------------------------------------
// work_permit_document — child certificates attached to a work_permit
// (design.md §"Schema Drizzle" Slice C, PR5). NO `riskLevel` field anywhere in
// this schema — `.strict()` below rejects it if a caller tries (task 5.8).
// NO `createdBy` field either — see migration 0170's header note (design.md's
// literal table shape has no authorship column on this table).
// ---------------------------------------------------------------------------

export const WorkPermitDocumentCreateSchema = z
  .object({
    documentTypeId: z.string().uuid('documentTypeId must be a valid UUID'),
    documentNumber: z.string().max(128, 'documentNumber must be at most 128 characters').optional(),
    fileId: z.string().uuid('fileId must be a valid UUID').optional(),
    issuedAt: z.string().datetime({ message: 'issuedAt must be an ISO datetime string' }).optional(),
    notes: z.string().max(2000, 'notes must be at most 2000 characters').optional(),
  })
  .strict();
export type WorkPermitDocumentCreate = z.infer<typeof WorkPermitDocumentCreateSchema>;

export const WorkPermitDocumentResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  workPermitId: z.string().uuid(),
  documentTypeId: z.string().uuid(),
  documentNumber: z.string().nullable(),
  fileId: z.string().uuid().nullable(),
  issuedAt: z.string().datetime().or(z.date()).nullable(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
});
export type WorkPermitDocumentResponse = z.infer<typeof WorkPermitDocumentResponseSchema>;

// ---------------------------------------------------------------------------
// work_permit_link — N:N "permisos vinculados" (design.md §"Schema Drizzle"
// Slice C, PR5). fromPermitId comes from the URL path, never the body.
// ---------------------------------------------------------------------------

export const WorkPermitLinkCreateSchema = z
  .object({
    toPermitId: z.string().uuid('toPermitId must be a valid UUID'),
  })
  .strict();
export type WorkPermitLinkCreate = z.infer<typeof WorkPermitLinkCreateSchema>;

export const WorkPermitLinkResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  fromPermitId: z.string().uuid(),
  toPermitId: z.string().uuid(),
  createdAt: z.string().datetime().or(z.date()),
});
export type WorkPermitLinkResponse = z.infer<typeof WorkPermitLinkResponseSchema>;

// ---------------------------------------------------------------------------
// work_permit_transition — FSM transitions (design.md §"Schema Drizzle"
// Slice D, PR6). WORK_PERMIT_TRANSITION_TARGET_STATES is duplicated (not
// imported) from packages/modules/servicios/src/domain/work-permit-fsm.ts —
// same cross-package posture already used for PTW_HAZARD_KEYS above
// (contracts-* never depends on packages/modules/*). EN_ELABORACION is
// EXCLUDED here — it is the FSM's only initial state, never a transition
// TARGET (no seeded edge points into it).
//
// `reason`/`closureKindId` are BOTH optional here — the conditional
// requirement (reason for SUSPENDIDO/RECHAZADO/CANCELADO/
// CANCELADO_A_REPROGRAMAR; closureKindId for CERRADO only) is enforced in
// TransitionWorkPermitUseCase (application layer), NOT via Zod
// `.superRefine()` — same posture as CreateVehicleDocumentUseCase's
// ExpirationRequiredError (a cross-field rule tied to a DB-resolved catalog
// row, not expressible as a pure shape check).
// ---------------------------------------------------------------------------

export const WORK_PERMIT_TRANSITION_TARGET_STATES = [
  'EN_REVISION',
  'APROBADO',
  'EN_EJECUCION',
  'SUSPENDIDO',
  'CERRADO',
  'RECHAZADO',
  'CANCELADO',
  'CANCELADO_A_REPROGRAMAR',
] as const;
export const WorkPermitTransitionTargetStateSchema = z.enum(WORK_PERMIT_TRANSITION_TARGET_STATES, {
  errorMap: () => ({ message: `toState must be one of: ${WORK_PERMIT_TRANSITION_TARGET_STATES.join(', ')}` }),
});
export type WorkPermitTransitionTargetState = z.infer<typeof WorkPermitTransitionTargetStateSchema>;

export const WorkPermitTransitionCreateSchema = z
  .object({
    toState: WorkPermitTransitionTargetStateSchema,
    reason: z.string().min(1, 'reason cannot be empty').max(2000, 'reason must be at most 2000 characters').optional(),
    closureKindId: z.string().uuid('closureKindId must be a valid UUID').optional(),
  })
  .strict();
export type WorkPermitTransitionCreate = z.infer<typeof WorkPermitTransitionCreateSchema>;

export const WorkPermitTransitionResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  workPermitId: z.string().uuid(),
  fromStateId: z.string().uuid().nullable(),
  toStateId: z.string().uuid(),
  byUserId: z.string().uuid(),
  /**
   * cross-tenant-kernel-mechanism PR5 (migration 0183) — the ACTOR's own
   * company. `companyId` above stays the OWNER's (the row belongs to the
   * permit's lineage); for a counterparty-executed APROBADO transition
   * `byUserId` is a foreign-tenant user, so without this field the timeline
   * cannot render "Aprobado por X (Operadora)" — see design.md CT7.
   * `null` for pre-PR5 rows that predate the column (backfilled to
   * `companyId` at the DB layer, but the Zod shape stays nullable so an
   * un-backfilled read never fails validation).
   */
  byCompanyId: z.string().uuid().nullable(),
  reason: z.string().nullable(),
  closureKindId: z.string().uuid().nullable(),
  occurredAt: z.string().datetime().or(z.date()),
});
export type WorkPermitTransitionResponse = z.infer<typeof WorkPermitTransitionResponseSchema>;

/**
 * huecos-de-lectura-frontend (Slice H / PR10), gap 7 — GET
 * /locx/work-permits/:id/transitions. `WorkPermitTransitionRepository.listFor`
 * has existed since PR6 (D) but was never exposed over HTTP — the detail
 * screen's timeline needs the append-only transition history.
 */
export const WorkPermitTransitionListResponseSchema = z.object({
  items: z.array(WorkPermitTransitionResponseSchema),
});
export type WorkPermitTransitionListResponse = z.infer<typeof WorkPermitTransitionListResponseSchema>;

// ---------------------------------------------------------------------------
// work_permit_shift_signoff — append-only shift/turno signoff grid (design.md
// §Slice E, PR7). ONE row per casilla — covers BOTH the 7x2x3 calendar grid
// (direction='unico') and the change-of-actor revalidation
// (direction='saliente'/'entrante') per spec locx-work-permit-evidence.
//
// signedByUserId is NEVER in the request body — TenantContext (KC-3.5).
// shiftIndex's upper bound (ptwType.shiftsPerDay) is validated at the
// APPLICATION layer (RecordShiftSignoffUseCase), not here — Zod only enforces
// the shape-level positive-integer constraint.
// ---------------------------------------------------------------------------

export const PTW_SIGNOFF_DIRECTIONS = ['saliente', 'entrante', 'unico'] as const;
export const PtwSignoffDirectionSchema = z.enum(PTW_SIGNOFF_DIRECTIONS, {
  errorMap: () => ({ message: `direction must be one of: ${PTW_SIGNOFF_DIRECTIONS.join(', ')}` }),
});
export type PtwSignoffDirection = z.infer<typeof PtwSignoffDirectionSchema>;

export const WorkPermitShiftSignoffCreateSchema = z
  .object({
    periodDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'periodDate must be an ISO date (YYYY-MM-DD)'),
    shiftIndex: z.coerce.number().int().positive('shiftIndex must be a positive integer'),
    authorityRoleId: z.string().uuid('authorityRoleId must be a valid UUID'),
    direction: PtwSignoffDirectionSchema,
    signedPersonId: z.string().uuid('signedPersonId must be a valid UUID').optional(),
    notes: z.string().max(2000, 'notes must be at most 2000 characters').optional(),
  })
  .strict();
export type WorkPermitShiftSignoffCreate = z.infer<typeof WorkPermitShiftSignoffCreateSchema>;

export const WorkPermitShiftSignoffResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  workPermitId: z.string().uuid(),
  periodDate: z.string(),
  shiftIndex: z.number().int().positive(),
  authorityRoleId: z.string().uuid(),
  direction: PtwSignoffDirectionSchema,
  signedByUserId: z.string().uuid(),
  signedPersonId: z.string().uuid().nullable(),
  signedAt: z.string().datetime().or(z.date()),
  notes: z.string().nullable(),
});
export type WorkPermitShiftSignoffResponse = z.infer<typeof WorkPermitShiftSignoffResponseSchema>;

export const WorkPermitShiftSignoffListResponseSchema = z.object({
  items: z.array(WorkPermitShiftSignoffResponseSchema),
});
export type WorkPermitShiftSignoffListResponse = z.infer<typeof WorkPermitShiftSignoffListResponseSchema>;

// ---------------------------------------------------------------------------
// work_permit_atmosphere_reading / ..._reading_value — header + row-per-gas
// (design.md §Slice E, PR7). `result` is NEVER in the request body — computed
// server-side by domain/atmosphere-result.ts (task 7.3/7.8). measuredByUserId
// is NEVER in the request body — TenantContext (KC-3.5). The gas itself is a
// ptw_field_definition row (groupKey='gas'); adding a new gas needs no new
// contract shape, only a new ptw_field_definition config row.
// ---------------------------------------------------------------------------

export const WorkPermitAtmosphereReadingValueInputSchema = z
  .object({
    ptwFieldDefinitionId: z.string().uuid('ptwFieldDefinitionId must be a valid UUID'),
    valueNum: z.coerce.number({ invalid_type_error: 'valueNum must be a number' }),
  })
  .strict();
export type WorkPermitAtmosphereReadingValueInput = z.infer<typeof WorkPermitAtmosphereReadingValueInputSchema>;

export const WorkPermitAtmosphereReadingCreateSchema = z
  .object({
    measuredAt: z.string().datetime({ message: 'measuredAt must be an ISO datetime string' }),
    instrument: z.string().max(128, 'instrument must be at most 128 characters').optional(),
    locationNote: z.string().max(256, 'locationNote must be at most 256 characters').optional(),
    values: z
      .array(WorkPermitAtmosphereReadingValueInputSchema)
      .min(1, 'at least one gas reading value is required'),
  })
  .strict();
export type WorkPermitAtmosphereReadingCreate = z.infer<typeof WorkPermitAtmosphereReadingCreateSchema>;

export const PTW_ATMOSPHERE_RESULTS = ['apto', 'no_apto'] as const;
export const PtwAtmosphereResultSchema = z.enum(PTW_ATMOSPHERE_RESULTS, {
  errorMap: () => ({ message: `result must be one of: ${PTW_ATMOSPHERE_RESULTS.join(', ')}` }),
});
export type PtwAtmosphereResult = z.infer<typeof PtwAtmosphereResultSchema>;

export const WorkPermitAtmosphereReadingValueResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  readingId: z.string().uuid(),
  ptwFieldDefinitionId: z.string().uuid(),
  valueNum: z.union([z.string(), z.number()]),
});
export type WorkPermitAtmosphereReadingValueResponse = z.infer<typeof WorkPermitAtmosphereReadingValueResponseSchema>;

export const WorkPermitAtmosphereReadingResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  workPermitId: z.string().uuid(),
  measuredAt: z.string().datetime().or(z.date()),
  measuredByUserId: z.string().uuid(),
  instrument: z.string().nullable(),
  locationNote: z.string().nullable(),
  result: PtwAtmosphereResultSchema,
  values: z.array(WorkPermitAtmosphereReadingValueResponseSchema),
});
export type WorkPermitAtmosphereReadingResponse = z.infer<typeof WorkPermitAtmosphereReadingResponseSchema>;

export const WorkPermitAtmosphereReadingListResponseSchema = z.object({
  items: z.array(WorkPermitAtmosphereReadingResponseSchema),
});
export type WorkPermitAtmosphereReadingListResponse = z.infer<typeof WorkPermitAtmosphereReadingListResponseSchema>;

// ---------------------------------------------------------------------------
// work_permit_crew_member — named crew per work_permit (design.md §Slice F,
// PR8). `personId`/`fullName`/`employeeNumber` are NEVER in the request body
// — they are resolved server-side from `employmentId` via IEmployeeLookup
// (task 8.5) and returned only in the response. No `riskLevel`/authorship
// field either — `.strict()` rejects anything not listed here.
// ---------------------------------------------------------------------------

export const WorkPermitCrewMemberCreateSchema = z
  .object({
    employmentId: z.string().uuid('employmentId must be a valid UUID'),
    roleNote: z.string().max(256, 'roleNote must be at most 256 characters').optional(),
  })
  .strict();
export type WorkPermitCrewMemberCreate = z.infer<typeof WorkPermitCrewMemberCreateSchema>;

/**
 * huecos-de-lectura-frontend (Slice H / PR10), gap 6 — one crew member's
 * habilitación, as resolved by `IEmployeeLookup.listCredentials` (PR8/F).
 * Same minimal projection the competency guard already uses internally
 * (`EmployeeCredentialRow` minus `personId`, redundant once nested under a
 * crew member) — never the raw `rrhh.employeeDocument` row.
 */
export const WorkPermitCrewMemberCredentialSchema = z.object({
  documentTypeId: z.string().uuid(),
  documentTypeName: z.string(),
  expiresAt: z.string().nullable(),
});
export type WorkPermitCrewMemberCredential = z.infer<typeof WorkPermitCrewMemberCredentialSchema>;

export const WorkPermitCrewMemberResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  workPermitId: z.string().uuid(),
  employmentId: z.string().uuid(),
  personId: z.string().uuid(),
  fullName: z.string(),
  employeeNumber: z.string(),
  roleNote: z.string().nullable(),
  createdAt: z.string().datetime().or(z.date()),
  /**
   * huecos-de-lectura-frontend (Slice H / PR10), gap 6 — badge data so the UI
   * can show vigente/vencida without a separate approval attempt. Empty array
   * when `employeeLookup` is not wired at composition time (graceful
   * degradation, same posture as every other optional external port in this
   * module) or when the person genuinely has zero credentials on file.
   */
  credentials: z.array(WorkPermitCrewMemberCredentialSchema),
});
export type WorkPermitCrewMemberResponse = z.infer<typeof WorkPermitCrewMemberResponseSchema>;

export const WorkPermitCrewMemberListResponseSchema = z.object({
  items: z.array(WorkPermitCrewMemberResponseSchema),
});
export type WorkPermitCrewMemberListResponse = z.infer<typeof WorkPermitCrewMemberListResponseSchema>;

// ---------------------------------------------------------------------------
// ats — LOCX ATS: contenido y ciclo de vida, PR1 (A). Own PTW-type key
// (`ptwTypeId`, design.md §Contratos Zod / proposal.md decision (b)):
// `workPermitId` XOR `ptwTypeId` — at least one is required. With
// `workPermitId` only, `ptwTypeId` is COPIED from the linked permit at
// create time. `ptwTypeId` is IMMUTABLE after creation — AtsUpdateSchema
// (a later PR in this chain) MUST NOT accept it.
//
// atsNumber/companyId/createdBy/createdAt are NEVER in the request body —
// atsNumber is server-generated (CreateAtsUseCase, "ATS-NNNN"), companyId/
// createdBy come from TenantContext (KC-3.5).
// ---------------------------------------------------------------------------

// ── ats_task_step (PR3/C) ────────────────────────────────────────────────
// "Pasos de tarea" — N repeatable rows, NOT EAV (design.md §Technical
// Approach): a fixed shape LOCX ships as code, not something the tenant
// configures field-by-field. `sequence` is NOT in the input: it is ALWAYS
// server-assigned from array position (AtsTaskStepRepository.replaceForAts).
// Editing is delete-and-reinsert — there is no per-row PATCH, so there is no
// separate "update" schema either.
export const AtsTaskStepCreateSchema = z
  .object({
    paso: z.string().min(1, 'paso is required').max(2000),
    peligro: z.string().max(2000).optional(),
    medidaControl: z.string().max(2000).optional(),
  })
  .strict();
export type AtsTaskStepCreate = z.infer<typeof AtsTaskStepCreateSchema>;

export const AtsTaskStepResponseSchema = z.object({
  id: z.string().uuid(),
  atsId: z.string().uuid(),
  sequence: z.number().int().positive(),
  paso: z.string(),
  peligro: z.string().nullable(),
  medidaControl: z.string().nullable(),
});
export type AtsTaskStepResponse = z.infer<typeof AtsTaskStepResponseSchema>;

// workPermitId XOR ptwTypeId: Zod only enforces "at least one" (422). The
// CONSISTENCY of the two together (mismatch) needs reading the permit row —
// that's 409 PTW_TYPE_MISMATCH in the use case, not 422 here.
//
// `fields` (PR2/B, design.md §D4) reuses `PtwFieldValueWriteInputSchema`
// VERBATIM — no `AtsFieldValue*Schema` is created anywhere. An exact mirror
// of an existing schema is the same schema, not a new one (root CLAUDE.md:
// never let two implementations of the same concept coexist).
//
// `taskSteps` (PR3/C) is optional — written atomically with the ats insert,
// same tx as `fields` (CreateAtsUseCase).
export const AtsCreateSchema = z
  .object({
    workPermitId: z.string().uuid('workPermitId must be a valid UUID').optional(),
    ptwTypeId: z.string().uuid('ptwTypeId must be a valid UUID').optional(),
    fields: z.array(PtwFieldValueWriteInputSchema).optional(),
    taskSteps: z.array(AtsTaskStepCreateSchema).optional(),
  })
  .strict()
  .refine((v) => v.workPermitId !== undefined || v.ptwTypeId !== undefined, {
    message: 'at least one of workPermitId or ptwTypeId is required',
    path: ['ptwTypeId'],
  });
export type AtsCreate = z.infer<typeof AtsCreateSchema>;

// PR2 (B) — `fields` REPLACE-ALL (delete-and-reinsert via
// PtwFieldValueRepository.deleteByAts → upsertValuesForAts), not a merge.
// PR3 (C) — `taskSteps` REPLACE-ALL the same way (AtsTaskStepRepository.
// replaceForAts), same tx. `ptwTypeId` is NEVER accepted — it is the
// immutable key of the field set (proposal.md decision (b) / spec "ATS
// Aggregate — Own PTW Type..."), exactly like `work_permit.ptwTypeId`.
// PR4 (D) — `workPermitId` relink: `null` desvincula, a UUID triggers the
// PTW_TYPE_MISMATCH guard in UpdateAtsUseCase (design.md §Data Flow) — safe
// ONLY because ptwTypeId no longer depends on this FK (proposal.md decision
// (b)'s payoff). A cancelled ATS rejects ANY PATCH with 409 ATS_CANCELLED,
// checked in the use case (not expressible here).
export const AtsUpdateSchema = z
  .object({
    workPermitId: z.string().uuid('workPermitId must be a valid UUID').nullable().optional(),
    fields: z.array(PtwFieldValueWriteInputSchema).optional(),
    taskSteps: z.array(AtsTaskStepCreateSchema).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'at least one field must be provided' });
export type AtsUpdate = z.infer<typeof AtsUpdateSchema>;

export const AtsResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  workPermitId: z.string().uuid().nullable(),
  ptwTypeId: z.string().uuid(),
  atsNumber: z.string(),
  /** PR4 (D) — one-way terminal flag (proposal.md §Approach). No reactivation path exists. */
  cancelled: z.boolean(),
  cancelledAt: z.string().datetime().or(z.date()).nullable(),
  cancelledBy: z.string().uuid().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string().datetime().or(z.date()),
});
export type AtsResponse = z.infer<typeof AtsResponseSchema>;

/**
 * PR4 (D), design.md §D6 — minimal summary of the linked `work_permit` for
 * `GET /locx/ats/:id`. Deliberately WITHOUT a resolved state name: that would
 * force the ATS route to mount `identity.state` lookup machinery the
 * frontend already gets for free from `GET /locx/work-permits/:id`.
 */
export const AtsLinkedWorkPermitSchema = z.object({
  id: z.string().uuid(),
  permitNumber: z.string(),
  ptwTypeId: z.string().uuid(),
});
export type AtsLinkedWorkPermit = z.infer<typeof AtsLinkedWorkPermitSchema>;

/**
 * Slice E — `GET /locx/ats` (list) row: base `AtsResponseSchema` plus a
 * resolved `createdByName` and the linked-permit summary, so the listado
 * never renders `createdBy`/`workPermitId` as raw uuids (apps/frontend/
 * CLAUDE.md, "never show raw keys" — the listado did exactly that until this
 * slice, real user report 2026-09-10). `createdByName` is null only when the
 * underlying user row is gone (broken FK, same posture as
 * WorkPermitResponseSchema's own `createdByName`).
 */
export const AtsListRowSchema = AtsResponseSchema.extend({
  createdByName: z.string().nullable(),
  workPermit: AtsLinkedWorkPermitSchema.nullable(),
});
export type AtsListRow = z.infer<typeof AtsListRowSchema>;

export const AtsListResponseSchema = z.object({
  items: z.array(AtsListRowSchema),
  totalCount: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  totalPages: z.number().int().nonnegative(),
});
export type AtsListResponse = z.infer<typeof AtsListResponseSchema>;

/**
 * GET /locx/ats/:id — PR2 (B) added `fields` (typed, never raw EAV rows —
 * D4 reuse of `PtwFieldValueResponseSchema`). PR3 (C) added `taskSteps`,
 * ordered by `sequence`. PR4 (D) adds `workPermit` — `null` when unlinked
 * (design.md §D6).
 */
export const AtsDetailResponseSchema = AtsResponseSchema.extend({
  fields: z.array(PtwFieldValueResponseSchema),
  taskSteps: z.array(AtsTaskStepResponseSchema),
  workPermit: AtsLinkedWorkPermitSchema.nullable(),
});
export type AtsDetailResponse = z.infer<typeof AtsDetailResponseSchema>;
